// src/features/merchantConfig/pages/MerchantConfigPage.jsx
//
// Top-level page for the Merchant Settings → Configuration screen.
//
// Responsibilities:
//   * Pulls `merchantId` from Redux (the rest of the portal already does this
//     via `useSelector` / localStorage — we follow that convention).
//   * Reads the selected category slug from the URL (`/settings/configuration/:slug`)
//     so the page is deep-linkable.
//   * Lays out the three-zone IA: header (with search), sidebar, detail panel.
//   * Fans out the 12 GETs via `useAllMerchantConfigs` so the sidebar dots
//     resolve in parallel and clicks are cache hits.
//   * Drives the active query via `useMerchantConfig` for the selected slug.
//   * Phase 2: wraps the panel + sidebar in an `EditStateProvider` keyed by
//     the active slug. Switching categories naturally resets edit state via
//     the React key remount. Intra-app navigation while dirty is intercepted
//     and routed through the UnsavedChangesPrompt.
//
// IA (per UI prompt §4):
//   [ Page header + search ]
//   [ Sidebar      |  Detail panel ]

import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

import CategorySidebar from "../components/CategorySidebar";
import ConfigDetailPanel from "../components/ConfigDetailPanel";
import GlobalConfigSearch from "../components/GlobalConfigSearch";
import { SidebarSkeleton } from "../components/states/LoadingSkeleton";
import UnsavedChangesPrompt from "../components/UnsavedChangesPrompt";
import { useAllMerchantConfigs } from "../hooks/useAllMerchantConfigs";
import { useMerchantConfig, merchantConfigQueryKey } from "../hooks/useMerchantConfig";
import { useConfigSearch } from "../hooks/useConfigSearch";
import {
  MERCHANT_CONFIG_CATALOG,
  findCatalogEntry,
} from "../lib/configKeyCatalog";
import {
  EditStateProvider,
  useEditState,
} from "../state/EditStateContext";

/* ---------- Constants ---------- */

const BASE_ROUTE = "/settings/configuration";

/** valueField lookup, by enum key (used by useConfigSearch). */
const VALUE_FIELD_BY_KEY = Object.fromEntries(
  MERCHANT_CONFIG_CATALOG.map((e) => [e.key, e.valueField])
);

/* ---------- Page ---------- */

export default function MerchantConfigPage() {
  // merchantId resolution: redux first (set by authSlice on login), then fall
  // back to localStorage for the case where the user has hard-reloaded before
  // the rehydration effect has run.
  const reduxMerchantId = useSelector((state) => state?.auth?.merchantId);
  const merchantId =
    reduxMerchantId ||
    (typeof window !== "undefined" ? localStorage.getItem("merchantId") : null);

  const navigate = useNavigate();
  const { slug: rawSlug } = useParams();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");

  // Resolve selected catalog entry from the URL slug. Fall back to undefined
  // so the detail panel shows the "select a category" welcome card.
  const selectedEntry = useMemo(() => findCatalogEntry(rawSlug), [rawSlug]);
  const selectedSlug = selectedEntry?.slug ?? null;

  // Fan out the 12 probes for sidebar dots + search corpus.
  const allConfigs = useAllMerchantConfigs({ merchantId });

  // Drive the active detail-panel query for the selected slug.
  const activeQuery = useMerchantConfig({
    merchantId,
    configKey: selectedEntry?.key,
    enabled: Boolean(selectedEntry && merchantId),
  });

  // Synthesize a uniform query state for ConfigDetailPanel.
  const queryState = useMemo(() => {
    if (!selectedEntry) {
      return {
        status: "loading",
        isFetching: false,
        data: null,
        error: null,
      };
    }
    let status = "loading";
    if (activeQuery.isSuccess) status = "success";
    else if (activeQuery.isError) status = "error";
    return {
      status,
      isFetching: activeQuery.isFetching,
      data: activeQuery.data ?? null,
      error: activeQuery.error ?? null,
    };
  }, [
    selectedEntry,
    activeQuery.isSuccess,
    activeQuery.isError,
    activeQuery.isFetching,
    activeQuery.data,
    activeQuery.error,
  ]);

  // Plain navigation — used both by the dirty-aware guard below and by the
  // search-result picker (search results aren't behind the dirty guard yet;
  // the user explicitly committed by picking a result).
  const navigateToCategory = useCallback(
    (slug) => {
      navigate(`${BASE_ROUTE}/${slug}`);
    },
    [navigate]
  );

  // Refresh just the currently selected category — explicitly invalidates the
  // cache so we always go back to the server (per UI prompt §6 step 3).
  const onRefreshActive = useCallback(() => {
    if (!selectedEntry || !merchantId) return;
    queryClient.invalidateQueries({
      queryKey: merchantConfigQueryKey(merchantId, selectedEntry.key),
      exact: true,
    });
  }, [queryClient, merchantId, selectedEntry]);

  // Global search across loaded categories.
  const searchState = useConfigSearch({
    query: searchInput,
    results: allConfigs.results,
    valueFieldByKey: VALUE_FIELD_BY_KEY,
  });

  // Contact-support CTA stub — left intentionally as a navigation to the
  // existing Support route. UI prompt §15 calls this out as an open question;
  // routing to /support/tickets is the safest default for Phase 1.
  const onContactSupport = useCallback(() => {
    navigate("/support/tickets");
  }, [navigate]);

  // Missing-merchant gate (cold load before auth rehydration etc.)
  if (!merchantId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 p-4 sm:p-6">
        <div className="mx-auto max-w-6xl">
          <header className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Account Configuration
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              View all configuration categories stored for your account.
            </p>
          </header>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            We couldn't determine which merchant account to load. Please sign
            in again or refresh the page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        {/* Page header */}
        <header className="mb-5 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Account Configuration
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              View every configuration category stored for your account.
            </p>
          </div>
          <GlobalConfigSearch
            inputValue={searchInput}
            onInputChange={setSearchInput}
            searchState={searchState}
            onPickResult={navigateToCategory}
          />
        </header>

        {/* Body: sidebar + detail panel, wrapped in a per-category edit-state
            provider. The `key` prop makes React remount the subtree whenever
            the user navigates to a different category, so edit-mode state is
            never accidentally shared across categories. */}
        <EditStateProvider
          key={selectedSlug ?? "__no_selection__"}
          valueField={selectedEntry?.valueField}
        >
          <EditAwareLayout
            merchantId={merchantId}
            selectedEntry={selectedEntry}
            selectedSlug={selectedSlug}
            queryState={queryState}
            allConfigs={allConfigs}
            onContactSupport={onContactSupport}
            onRefreshActive={onRefreshActive}
            navigateToCategory={navigateToCategory}
          />
        </EditStateProvider>
      </div>
    </div>
  );
}

/* ---------- EditAwareLayout ---------- */

/**
 * Renders the sidebar + detail panel and intercepts intra-app category
 * switches while edit mode is dirty. Lives inside the EditStateProvider so it
 * can read `isDirty` directly.
 *
 * @param {{
 *   merchantId: string,
 *   selectedEntry: import('../lib/configKeyCatalog').MerchantConfigCatalogEntry | undefined,
 *   selectedSlug: string | null,
 *   queryState: { status: string, isFetching: boolean, data: object | null, error: object | null },
 *   allConfigs: { results: Array<object>, isAllResolved: boolean },
 *   onContactSupport: () => void,
 *   onRefreshActive: () => void,
 *   navigateToCategory: (slug: string) => void,
 * }} props
 */
function EditAwareLayout({
  merchantId,
  selectedEntry,
  selectedSlug,
  queryState,
  allConfigs,
  onContactSupport,
  onRefreshActive,
  navigateToCategory,
}) {
  const editState = useEditState();

  // Pending slug we'd navigate to once the user discards their edits.
  // `null` means no navigation in flight.
  const [pendingSlug, setPendingSlug] = useState(null);

  const onSelectCategory = useCallback(
    (slug) => {
      // No-op if user clicks the active slug.
      if (slug === selectedSlug) return;

      // Dirty edit mode → ask before discarding. If the answer is "discard"
      // we'll navigate; "stay" cancels the navigation.
      if (editState.isDirty) {
        setPendingSlug(slug);
        return;
      }

      navigateToCategory(slug);
    },
    [editState.isDirty, selectedSlug, navigateToCategory]
  );

  const onCancelPendingNav = useCallback(() => {
    setPendingSlug(null);
  }, []);

  const onConfirmPendingNav = useCallback(() => {
    const target = pendingSlug;
    setPendingSlug(null);
    // Reset edit state before navigating. Even though the EditStateProvider
    // remounts on slug change (so state resets anyway), exiting edit mode
    // first means there's no flash of dirty UI during the transition.
    editState.cancelEdit();
    if (target) navigateToCategory(target);
  }, [pendingSlug, editState, navigateToCategory]);

  return (
    <>
      <div className="flex flex-col gap-5 md:flex-row">
        {!allConfigs.isAllResolved && allConfigs.results.every((r) => r.status === "loading") ? (
          <aside className="hidden md:block md:w-64 md:shrink-0">
            <SidebarSkeleton />
          </aside>
        ) : (
          <CategorySidebar
            probes={allConfigs.results}
            selectedSlug={selectedSlug}
            onSelect={onSelectCategory}
          />
        )}

        <main className="flex-1 min-w-0">
          <ConfigDetailPanel
            catalogEntry={selectedEntry}
            merchantId={merchantId}
            queryState={queryState}
            onRefresh={onRefreshActive}
            onContactSupport={onContactSupport}
          />
        </main>
      </div>

      {/* Intra-app navigation guard. Browser-level tab-close guard is wired
          separately inside ConfigDetailPanel via useBeforeUnloadGuard. */}
      <UnsavedChangesPrompt
        open={pendingSlug !== null}
        dirtyCount={editState.dirtyKeys.size}
        onCancel={onCancelPendingNav}
        onDiscard={onConfirmPendingNav}
      />
    </>
  );
}
